/**
 * LRUCacheEntry - Type wrapper for LRU cache tracking
 * 
 * Wraps cache values with access time tracking for proper LRU eviction.
 * Used to upgrade the existing FIFO eviction to true LRU behavior.
 */

/**
 * Cache entry with LRU tracking metadata
 */
export interface LRUCacheEntry<T> {
  /** The cached value */
  value: T;
  /** Timestamp of last access (Date.now()) */
  lastAccessed: number;
  /** Timestamp of creation */
  createdAt: number;
  /** Number of times this entry has been accessed */
  hitCount: number;
}

/**
 * Create a new LRU cache entry
 */
export function createLRUEntry<T>(value: T): LRUCacheEntry<T> {
  const now = Date.now();
  return {
    value,
    lastAccessed: now,
    createdAt: now,
    hitCount: 0
  };
}

/**
 * Touch an entry (update access time and increment hit count)
 */
export function touchLRUEntry<T>(entry: LRUCacheEntry<T>): void {
  entry.lastAccessed = Date.now();
  entry.hitCount++;
}

/**
 * Find the least recently used key in a Map of LRU entries
 */
export function findLRUKey<T>(cache: Map<string, LRUCacheEntry<T>>): string | null {
  let lruKey: string | null = null;
  let lruTime = Infinity;
  
  for (const [key, entry] of cache) {
    if (entry.lastAccessed < lruTime) {
      lruTime = entry.lastAccessed;
      lruKey = key;
    }
  }
  
  return lruKey;
}

/**
 * Evict least recently used entries until cache is within size limit
 * 
 * @param cache - Map with LRU entries
 * @param maxSize - Maximum allowed size
 * @returns Number of entries evicted
 */
export function evictLRUEntries<T>(
  cache: Map<string, LRUCacheEntry<T>>,
  maxSize: number
): number {
  let evicted = 0;
  
  while (cache.size > maxSize) {
    const lruKey = findLRUKey(cache);
    if (lruKey) {
      cache.delete(lruKey);
      evicted++;
    } else {
      break;
    }
  }
  
  return evicted;
}

/**
 * Get cache statistics
 */
export function getLRUCacheStats<T>(cache: Map<string, LRUCacheEntry<T>>): {
  size: number;
  totalHits: number;
  avgHitsPerEntry: number;
  oldestEntryAge: number;
  newestEntryAge: number;
} {
  const now = Date.now();
  let totalHits = 0;
  let oldestCreated = now;
  let newestCreated = 0;
  
  for (const entry of cache.values()) {
    totalHits += entry.hitCount;
    if (entry.createdAt < oldestCreated) oldestCreated = entry.createdAt;
    if (entry.createdAt > newestCreated) newestCreated = entry.createdAt;
  }
  
  return {
    size: cache.size,
    totalHits,
    avgHitsPerEntry: cache.size > 0 ? totalHits / cache.size : 0,
    oldestEntryAge: cache.size > 0 ? now - oldestCreated : 0,
    newestEntryAge: cache.size > 0 ? now - newestCreated : 0
  };
}
